// Schema.org JSON-LD builders, ported from the Angular StructuredData service
// (app/assets/javascripts/services/structured_data.js).
import Keys from '@/constants/Keys';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const absUrl = (path = '') => `${Keys.odas.host}${path}`;

const baseNodes = () => [
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: absUrl(),
    name: 'One Degree',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${absUrl('/search')}?q={search_term_string}`,
      'query-input': 'required name=search_term_string'
    }
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    url: absUrl(),
    logo: absUrl('/logo.png'),
    sameAs: [
      'https://www.facebook.com/1degree.org',
      'https://twitter.com/1deg',
      'https://github.com/1deg/',
      'https://www.linkedin.com/company/one-degree-inc-',
      'https://www.instagram.com/1deg/',
      'https://www.youtube.com/user/1degorg'
    ]
  }
];

const scheduleMetadata = (schedule) => {
  const ret = [];
  DAYS.forEach((day) => {
    const dayLowerCase = day.toLowerCase();
    if (schedule[`${dayLowerCase}_start`] && schedule[`${dayLowerCase}_end`]) {
      ret.push({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: [day],
        opens: schedule[`${dayLowerCase}_start`],
        closes: schedule[`${dayLowerCase}_end`]
      });
    }
  });
  return ret;
};

const locationMetadata = (location) => {
  const ret = {
    '@type': 'PostalAddress',
    addressLocality: location.city,
    addressRegion: location.state,
    addressCountry: 'US'
  };
  if (location.name) {
    ret.name = location.name;
  }
  if (location.address) {
    ret.streetAddress = location.address + (location.unit ? ` ${location.unit}` : '');
  }
  if (location.zip_code) {
    ret.postalCode = location.zip_code;
  }
  if (location.schedule) {
    ret.hoursAvailable = scheduleMetadata(location.schedule);
  }
  return ret;
};

export const organizationMetadata = (organization) => {
  const node = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': absUrl(`/org/${organization.slug}`),
    name: organization.name,
    url: absUrl(`/org/${organization.slug}`),
    sameAs: organization.website,
    description: organization.description,
    image: absUrl('/logo.png'), // required by google tester
    geo: {
      '@type': 'GeoCoordinates',
      latitude: organization.lat,
      longitude: organization.lng
    }
  };
  if (organization.locations?.length) {
    node.location = organization.locations.map(locationMetadata);
  }
  if (organization.phones) {
    node.telephone = organization.phones.map((phone) => `+1-${phone.digits}`);
  }
  return node;
};

export const opportunityMetadata = (opportunity) => {
  const node = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': absUrl(`/opp/${opportunity.slug}`),
    name: opportunity.title,
    url: absUrl(`/opp/${opportunity.slug}`),
    description: opportunity.description,
    image: absUrl('/logo.png') // required by google tester
  };
  if (opportunity.organization?.slug) {
    node.provider = {
      '@id': absUrl(`/org/${opportunity.organization.slug}`)
    };
  }
  if (opportunity.locations?.length) {
    node.location = opportunity.locations.map(locationMetadata);
  }
  return node;
};

export const structuredDataForResource = (resource) => {
  const nodes = baseNodes();
  switch (resource?.resource_type) {
    case 'Organization':
      nodes.push(organizationMetadata(resource));
      break;
    case 'Opportunity':
      nodes.push(opportunityMetadata(resource));
      break;
    default:
      break;
  }
  return nodes;
};
